/**
 * Relation de Chasles et colinéarité — calculs purs sur des points entiers.
 */

import type { Pt } from '../../ui/frame';

export interface Vec {
  x: number;
  y: number;
}

export function vec(p: Pt, q: Pt): Vec {
  return { x: q.x - p.x, y: q.y - p.y };
}

export function add(u: Vec, v: Vec): Vec {
  return { x: u.x + v.x, y: u.y + v.y };
}

/** déterminant x·y′ − x′·y : nul si et seulement si u et v sont colinéaires */
export function det(u: Vec, v: Vec): number {
  return u.x * v.y - v.x * u.y;
}

/** nombre en KaTeX, entre parenthèses s'il est négatif dans un produit */
export function texFactor(n: number): string {
  return n < 0 ? `(-${Math.abs(n)})` : String(n);
}

/** vecteur colonne KaTeX */
export function texColumn(a: string | number, b: string | number): string {
  return `\\begin{pmatrix} ${a} \\\\ ${b} \\end{pmatrix}`;
}

/** « 3 + (−1) » pour une somme de coordonnées */
export function texSum(a: number, b: number): string {
  return `${a} + ${texFactor(b)}`;
}

export type Pts = Record<'A' | 'B' | 'C', Pt>;

export interface VectorGoal {
  id: string;
  text: string;
  check: (p: Pts) => boolean;
}

export const GOALS: readonly VectorGoal[] = [
  {
    id: 'ac-5-0',
    text: 'Déplace C pour que le vecteur AC ait pour coordonnées (5 ; 0).',
    check: (p) => {
      const ac = vec(p.A, p.C);
      return ac.x === 5 && ac.y === 0;
    },
  },
  {
    id: 'aligned',
    text: 'Aligne les points A, B et C (sans les confondre).',
    check: (p) => det(vec(p.A, p.B), vec(p.A, p.C)) === 0,
  },
  {
    id: 'midpoint',
    text: 'Rends les vecteurs AB et BC égaux : B devient le milieu de [AC].',
    check: (p) => {
      const ab = vec(p.A, p.B);
      const bc = vec(p.B, p.C);
      return ab.x === bc.x && ab.y === bc.y;
    },
  },
];
